/* De mediabibliotheek van de site (ADR 0024): de beelden en pdf's in de bucket `website-media`, met
 * per bestand een rij in `website.media_files`. Een rij heeft een eigen vorm (geen titel, geen slug,
 * geen publicatie) en hoort bij een bestand in de opslag. Daarom staat dit naast de soorten content in
 * ./website en niet erin.
 *
 * Hoe het in elkaar zit:
 *   - Het bestand staat in de bucket als `media/<storage_path>`. In de rij staat `storage_path` zonder
 *     die map, en in de content staat een beeld als `/media/<storage_path>`. De site zet `/media/…` om
 *     naar de opslag (next.config.ts in de site-repo).
 *   - De site leest van `media_files` het pad en de beschrijving (`alt_text`), sinds het besluit van
 *     10-10-2026 (site-repo, tak v2-logo-en-alt, 11-10-2026: getMediaAltTexts in lib/content.ts). De
 *     beschrijving wordt de beschrijving van het beeld voor wie het niet ziet, op de plekken waar de
 *     pagina zelf alleen een titel had: de foto bovenaan een pagina of een nieuws- of kennisartikel (niet
 *     die van de homepage, die blijft leeg), de brede foto op de pagina van een oplossing, dienst of
 *     branche, de tekening op de homepage, en een beeld in het schuifbeeld zonder eigen beschrijving. Op
 *     kaarten, teamfoto's en logo's blijft de titel of de naam de beschrijving. De naam (`display_name`)
 *     gebruikt de site niet.
 *   - Na een gewijzigde beschrijving ververst de site daarom (wijzigMediaEnVervers in ../ververs); na
 *     alleen een andere naam niet, en na uploaden ook niet: een nieuw bestand verandert niets op de site
 *     tot het ergens gebruikt wordt.
 *
 * De regels hier:
 *   - Er wordt niets verwijderd: geen bestand en geen rij. Een bestand kan op een pagina in gebruik zijn.
 *   - Er wordt niets overschreven. Uploaden gaat met `upsert: false`: bestaat het pad al, dan is dat een
 *     fout. Dat is ook wat de rechten toelaten: voor een upsert is leesrecht op de opslag nodig, en dat
 *     heeft het CRM-team op deze bucket niet (migratie 099 geeft insert, update en delete).
 *   - Vóór het uploaden wordt gekeurd wat de bucket ook afdwingt: de soort bestand en hooguit 15 MB.
 *   - Eerst het bestand, dan de rij. Lukt de upload maar de rij niet, dan staat er een bestand zonder
 *     rij. Dat is geen fout die iets terugdraait: de uitkomst noemt het pad, er wordt niets verwijderd,
 *     en `maakRij` maakt de rij alsnog aan zonder het bestand opnieuw te uploaden.
 *   - Van een bestaande rij zijn de naam en de beschrijving te wijzigen, en alleen wat is gewijzigd,
 *     wordt geschreven. Het pad ligt vast: de content verwijst ernaar. De labels (`tags`) worden
 *     getoond en nooit geschreven.
 *
 * De client komt als argument binnen, zoals in ./website, zodat de toetsen hem kunnen nabootsen. */

import { VOORVOEGSEL } from './beeldpad';
import { SOORTEN, maakSlug, mediaPad, type Rij, type WebsiteClient } from './website';

export const TABEL = 'media_files';
export const BUCKET = 'website-media';
/** De map in de bucket waarin alle media staan. `storage_path` is het pad daarbinnen. */
export const MAP = 'media';
/** Waarmee een beeld in de content begint (./beeldpad). */
export { VOORVOEGSEL };
/* Een voorbeeld in de CRM komt rechtstreeks uit de bucket. Op de site zelf loopt dit via
 * greenvision.energy/media/*; de CRM is intern en mag de opslag direct aanspreken. */
export const OPSLAG = 'https://fxlorumfwzrdwiftbalo.supabase.co/storage/v1/object/public/website-media';
const KOLOMMEN = 'id, storage_path, display_name, alt_text, tags, created_at, updated_at';

export interface MediaBestand {
  id: string;
  /** Het pad in de bucket, zonder de map `media/`. Uniek. */
  storage_path: string;
  display_name: string | null;
  alt_text: string | null;
  tags: string[] | null;
  created_at: string;
  updated_at: string;
}

/** De opslag zoals de Supabase-client hem aanbiedt: alleen wat hier nodig is. */
export interface OpslagClient {
  storage: {
    from(bucket: string): {
      upload(pad: string, inhoud: Blob, opties: { contentType: string; cacheControl: string; upsert: boolean }): Promise<{
        data: { path?: string } | null;
        error: { message?: string; status?: number; statusCode?: string } | null;
      }>;
    };
  };
}
export type MediaClient = WebsiteClient & OpslagClient;

/* ── Wat de bucket aanneemt ────────────────────────────────────────────────── */

export interface BestandSoort {
  mime: string;
  /** De extensie die een nieuw bestand krijgt. */
  ext: string;
  /** Extensies waaraan de soort te herkennen is als de browser geen soort meegeeft. */
  herken: string[];
  beeld: boolean;
}

/** De soorten die de bucket aanneemt (`allowed_mime_types`, migratie 099). Geen svg: via het eigen
 *  domein zou een svg met een script erin in de site zelf draaien. */
export const BESTANDSSOORTEN: BestandSoort[] = [
  { mime: 'image/png', ext: 'png', herken: ['png'], beeld: true },
  { mime: 'image/jpeg', ext: 'jpg', herken: ['jpg', 'jpeg'], beeld: true },
  { mime: 'image/webp', ext: 'webp', herken: ['webp'], beeld: true },
  { mime: 'image/gif', ext: 'gif', herken: ['gif'], beeld: true },
  { mime: 'image/avif', ext: 'avif', herken: ['avif'], beeld: true },
  { mime: 'application/pdf', ext: 'pdf', herken: ['pdf'], beeld: false },
];
/** Hooguit 15 MB per bestand (`file_size_limit` van de bucket). */
export const MAX_BYTES = 15 * 1024 * 1024;
/** Vanaf hier waarschuwt de CRM dat een beeld zwaar is voor het web. Gemeten op de bibliotheek van
 *  10-10-2026: 73 van de 79 bestanden zijn kleiner, de helft is kleiner dan 0,4 MB. */
export const ZWAAR_BYTES = 2 * 1024 * 1024;
const NEEMT_AAN = 'De bibliotheek neemt png, jpg, webp, gif, avif en pdf.';
const BEELDEN = 'png, jpg, webp, gif of avif';

/** Voor het keuzevenster van de browser: alles wat de bucket aanneemt, of alleen de beelden. */
export const kiesbaar = (alleenBeelden = false): string =>
  BESTANDSSOORTEN.filter((s) => s.beeld || !alleenBeelden).map((s) => s.mime).join(',');

/** Wat er van een gekozen bestand nodig is: een `File` uit de browser past hierin. */
export interface Bestand extends Blob {
  name: string;
}

const extensie = (naam: string): string => {
  const punt = naam.lastIndexOf('.');
  return punt > 0 ? naam.slice(punt + 1).toLowerCase() : '';
};

/** De soort van een bestand, of null als de bucket het niet aanneemt. De browser geeft de soort mee;
 *  doet hij dat niet (leeg, of "zomaar een bestand"), dan beslist de extensie. */
export function soortVan(bestand: { name: string; type: string }): BestandSoort | null {
  const type = (bestand.type ?? '').toLowerCase();
  if (type && type !== 'application/octet-stream') return BESTANDSSOORTEN.find((s) => s.mime === type) ?? null;
  const ext = extensie(bestand.name);
  return BESTANDSSOORTEN.find((s) => s.herken.includes(ext)) ?? null;
}

/** De grootte in gewone taal: 380 kB of 4,3 MB. */
export function grootte(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export interface Keuring {
  soort: BestandSoort | null;
  /** Waarom dit bestand niet geüpload kan worden, of null. */
  fout: string | null;
  /** Houdt het uploaden niet tegen, maar is goed om te weten. */
  letOp: string | null;
}

/** Keurt een bestand vóór het uploaden op wat de bucket ook afdwingt: de soort en de grootte. Met
 *  `alleenBeelden` (de kiezer in een afbeeldingsveld) wordt ook een pdf geweigerd. */
export function keur(bestand: { name: string; type: string; size: number }, opties: { alleenBeelden?: boolean } = {}): Keuring {
  const soort = soortVan(bestand);
  const weiger = (fout: string): Keuring => ({ soort, fout, letOp: null });
  if (!soort) {
    const ext = extensie(bestand.name);
    if (ext === 'svg' || bestand.type === 'image/svg+xml') return weiger(`Een svg kan er niet in: daarin kan een script zitten. Sla het beeld op als png. ${NEEMT_AAN}`);
    if (ext === 'heic' || ext === 'heif') return weiger(`Dit is een foto in het formaat van een iPhone (heic). Zet hem eerst om naar jpg. ${NEEMT_AAN}`);
    return weiger(`Dit soort bestand kan er niet in${ext ? ` (.${ext})` : ''}. ${NEEMT_AAN}`);
  }
  if (opties.alleenBeelden && !soort.beeld) return weiger(`Een pdf is geen afbeelding. Kies hier een beeld: ${BEELDEN}.`);
  if (bestand.size === 0) return weiger('Dit bestand is leeg.');
  if (bestand.size > MAX_BYTES) return weiger(`Dit bestand is ${grootte(bestand.size)}. De bibliotheek neemt hooguit 15 MB per bestand.`);
  const zwaar = soort.beeld && bestand.size > ZWAAR_BYTES;
  return {
    soort, fout: null,
    letOp: zwaar ? `Dit beeld is ${grootte(bestand.size)}. Dat is zwaar voor een website: de CRM waarschuwt boven 2 MB. Uploaden kan, maar een kleiner bestand laadt sneller.` : null,
  };
}

/** De naam van een nieuw bestand in de bucket: `<tijdstempel>-<naam in kleine letters, cijfers en
 *  streepjes>.<extensie>`. De extensie volgt de soort, niet de oude naam (FOTO.JPEG wordt foto.jpg).
 *  Blijft er van de naam niets over, dan heet het bestand `bestand`. */
export function nieuwPad(naam: string, soort: BestandSoort, nu: number): string {
  const punt = naam.lastIndexOf('.');
  const kaal = punt > 0 ? naam.slice(0, punt) : naam;
  const net = maakSlug(kaal).slice(0, 80).replace(/-+$/, '') || 'bestand';
  return `${nu}-${net}.${soort.ext}`;
}

/* ── Het pad in de content en het adres in de opslag ───────────────────────── */

/** Zo staat een bestand uit de bibliotheek in de content: `/media/<storage_path>`. Een spatie in een
 *  oude naam wordt `%20`, zoals in de bestaande content (gemeten 10-10-2026: geen enkele verwijzing
 *  heeft een kale spatie). Een nieuwe naam heeft alleen kleine letters, cijfers en streepjes, en
 *  komt er dus letterlijk in. */
export const padInContent = (bestand: Pick<MediaBestand, 'storage_path'>): string =>
  `${VOORVOEGSEL}${bestand.storage_path.split('/').map(encodeURIComponent).join('/')}`;

/** Het adres van een bestand uit de bibliotheek in de opslag, voor een voorbeeld in de CRM. */
export const adresVan = (bestand: Pick<MediaBestand, 'storage_path'>): string => `${OPSLAG}${padInContent(bestand)}`;

/** Het adres in de opslag van een pad uit de content (`/media/…`), voor een voorbeeld in de CRM. Het
 *  pad kan al gecodeerd zijn (%20). */
export const adresVanPad = (pad: string): string => `${OPSLAG}${mediaPad(pad)}`;

const BEELD_EXTENSIES = BESTANDSSOORTEN.filter((s) => s.beeld).flatMap((s) => s.herken);
/** Een beeld is wat in een afbeeldingsveld past. Een pdf niet. */
export const isBeeld = (bestand: Pick<MediaBestand, 'storage_path'>): boolean => BEELD_EXTENSIES.includes(extensie(bestand.storage_path));

/** De naam waaronder een bestand in de lijst staat: de eigen naam, en anders het pad. */
export const naamVan = (bestand: Pick<MediaBestand, 'storage_path' | 'display_name'>): string => bestand.display_name?.trim() || bestand.storage_path;

const plat = (tekst: string): string => tekst.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Zoekt op naam, pad en beschrijving. Elk woord moet erin voorkomen; hoofdletters en accenten tellen niet. */
export function zoek<T extends Pick<MediaBestand, 'storage_path' | 'display_name' | 'alt_text'>>(bestanden: T[], vraag: string): T[] {
  const woorden = plat(vraag).split(/\s+/).filter(Boolean);
  if (woorden.length === 0) return bestanden;
  return bestanden.filter((b) => {
    const hooiberg = plat(`${b.display_name ?? ''} ${b.storage_path} ${b.alt_text ?? ''}`);
    return woorden.every((woord) => hooiberg.includes(woord));
  });
}

/* ── Lezen ─────────────────────────────────────────────────────────────────── */

function ofFout<T>(antwoord: { data: T | null; error: { message: string } | null }, wat: string): T {
  if (antwoord.error) throw new Error(`${wat}: ${antwoord.error.message}`);
  return antwoord.data as T;
}

/** De bibliotheek, het nieuwste bestand eerst. */
export async function haalMedia(client: WebsiteClient): Promise<MediaBestand[]> {
  const antwoord = await client.schema('website').from(TABEL).select(KOLOMMEN).order('created_at', { ascending: false });
  return ofFout<MediaBestand[]>(antwoord, 'Media lezen') ?? [];
}

/* ── Uploaden: eerst het bestand, dan de rij ───────────────────────────────── */

/** Wat er bij een bestand in de bibliotheek komt te staan. */
export interface Invoer {
  naam: string;
  beschrijving: string;
}

export interface Geupload {
  /** Het pad in de bibliotheek (`storage_path`). Het bestand staat in de opslag als `media/<pad>`. */
  pad: string;
  /** De rij in de bibliotheek, of null als het bestand er staat maar de rij niet is aangemaakt. */
  rij: MediaBestand | null;
  /** Waarom de rij er niet is. Gevuld als `rij` null is. */
  zonderRij: string | null;
}

const leeg = (waarde: string | null | undefined): string | null => (waarde ?? '').trim() || null;
const redenVan = (fout: unknown): string => (fout instanceof Error ? fout.message : String(fout));

/** Wat de opslag weigerde, in gewone taal. De code komt uit het antwoord van de opslag. */
function uploadFout(fout: { message?: string; status?: number; statusCode?: string }, pad: string): string {
  const code = String(fout.statusCode ?? fout.status ?? '');
  const tekst = fout.message ?? 'onbekende fout';
  if (code === '409' || /already exists|duplicate/i.test(tekst)) {
    return `Er staat al een bestand op ${MAP}/${pad}. Er is niets overschreven. Probeer het opnieuw: het bestand krijgt dan een nieuwe naam.`;
  }
  if (code === '413' || /maximum allowed size|too large/i.test(tekst)) return 'De opslag weigert het bestand: het is groter dan 15 MB.';
  if (code === '415' || /mime type/i.test(tekst)) return `De opslag weigert dit soort bestand. ${NEEMT_AAN}`;
  if (code === '403' || /row-level security|not authorized|unauthorized/i.test(tekst)) return 'Je hebt geen recht om naar de media te uploaden.';
  return `Uploaden is niet gelukt: ${tekst}`;
}

/** Maakt de rij in de bibliotheek voor een bestand dat al in de opslag staat. Veilig om te herhalen:
 *  staat de rij er al, dan komt die terug en wordt er niets overschreven. Uploadt niets. */
export async function maakRij(client: WebsiteClient, pad: string, invoer: Invoer): Promise<MediaBestand> {
  const website = client.schema('website');
  const rij = { storage_path: pad, display_name: leeg(invoer.naam), alt_text: leeg(invoer.beschrijving) };
  const antwoord = await website.from(TABEL).insert(rij).select(KOLOMMEN).maybeSingle();
  if (antwoord.error?.code === '23505') {
    const bestaand = ofFout<MediaBestand | null>(await website.from(TABEL).select(KOLOMMEN).eq('storage_path', pad).maybeSingle(), 'Media lezen');
    if (bestaand) return bestaand;
  }
  const gemaakt = ofFout<MediaBestand | null>(antwoord, 'Rij in de bibliotheek aanmaken');
  // RLS weigert zonder fout: geen rij terug betekent dat er niets is aangemaakt.
  if (!gemaakt) throw new Error('De rij in de bibliotheek is niet aangemaakt. Je hebt hier geen schrijfrecht.');
  return gemaakt;
}

/** Uploadt één bestand en maakt daarna de rij in de bibliotheek.
 *
 *  Gooit als het bestand is afgekeurd of de upload mislukt: dan staat er niets en is er niets
 *  geschreven. Lukt de upload en de rij niet, dan gooit dit niet: de uitkomst heeft `rij: null` en de
 *  reden in `zonderRij`, met het pad erbij. Er wordt dan niets opgeruimd. Het bestand staat er, en
 *  `maakRij` met hetzelfde pad maakt de rij alsnog aan. */
export async function uploadBestand(client: MediaClient, bestand: Bestand, invoer: Invoer, opties: { alleenBeelden?: boolean; nu?: number } = {}): Promise<Geupload> {
  const keuring = keur(bestand, opties);
  if (keuring.fout || !keuring.soort) throw new Error(keuring.fout ?? 'Dit bestand kan er niet in.');
  const soort = keuring.soort;
  const pad = nieuwPad(bestand.name, soort, opties.nu ?? Date.now());
  /* De opslag leest de soort uit het bestand zelf, niet uit `contentType`. Geeft de browser geen
   * soort mee, dan krijgt de inhoud de soort die bij de extensie hoort: anders weigert de bucket. */
  const inhoud = bestand.type === soort.mime ? bestand : bestand.slice(0, bestand.size, soort.mime);
  let antwoord: Awaited<ReturnType<ReturnType<OpslagClient['storage']['from']>['upload']>>;
  try {
    // upsert: false. Bestaat het pad al, dan weigert de opslag en wordt er niets vervangen.
    antwoord = await client.storage.from(BUCKET).upload(`${MAP}/${pad}`, inhoud, { contentType: soort.mime, cacheControl: '3600', upsert: false });
  } catch (fout) {
    throw new Error(`Uploaden is niet gelukt: ${redenVan(fout)}`);
  }
  if (antwoord.error) throw new Error(uploadFout(antwoord.error, pad));
  if (!antwoord.data) throw new Error('Uploaden is niet gelukt: de opslag gaf geen bevestiging terug.');
  const naam = leeg(invoer.naam) ?? bestand.name;
  try {
    return { pad, rij: await maakRij(client, pad, { ...invoer, naam }), zonderRij: null };
  } catch (fout) {
    return { pad, rij: null, zonderRij: redenVan(fout) };
  }
}

/** De zin bij een bestand dat in de opslag staat zonder rij in de bibliotheek. */
export const zonderRijZin = (pad: string, reden: string): string =>
  `Het bestand staat in de opslag als ${MAP}/${pad}, maar het staat nog niet in de bibliotheek (${reden}). Er is niets verwijderd. Upload het niet opnieuw: zet het alsnog in de bibliotheek.`;

/* ── De naam en de beschrijving van een bestaand bestand ───────────────────── */

/** Wat er aan een bestand verandert: alleen de kolommen die anders zijn. Het pad zit er nooit bij. */
export function wijziging(vorige: MediaBestand, concept: Invoer): Partial<Pick<MediaBestand, 'display_name' | 'alt_text'>> {
  const uit: Partial<Pick<MediaBestand, 'display_name' | 'alt_text'>> = {};
  if (leeg(concept.naam) !== leeg(vorige.display_name)) uit.display_name = leeg(concept.naam);
  if (leeg(concept.beschrijving) !== leeg(vorige.alt_text)) uit.alt_text = leeg(concept.beschrijving);
  return uit;
}

export interface Gewijzigd {
  rij: MediaBestand;
  /** De kolommen die zijn geschreven. Leeg = er was niets gewijzigd en er is niets geschreven. */
  geschreven: string[];
}

/** Wijzigt de naam of de beschrijving van een bestand. Alleen de gewijzigde kolommen gaan naar de
 *  database; het pad en de labels nooit. `geschreven` zegt welke: de site gebruikt de beschrijving
 *  (`alt_text`) en niet de naam, dus alleen na een gewijzigde beschrijving valt er iets te verversen. */
export async function wijzigMedia(client: WebsiteClient, vorige: MediaBestand, concept: Invoer): Promise<Gewijzigd> {
  const anders = wijziging(vorige, concept);
  const geschreven = Object.keys(anders);
  if (geschreven.length === 0) return { rij: vorige, geschreven };
  const antwoord = await client.schema('website').from(TABEL).update(anders).eq('id', vorige.id).select(KOLOMMEN).maybeSingle();
  const rij = ofFout<MediaBestand | null>(antwoord, 'Bestand opslaan');
  if (!rij) throw new Error('Het bestand is niet opgeslagen. Je hebt hier geen schrijfrecht, of de rij bestaat niet meer.');
  return { rij, geschreven };
}

/* ── Waar een bestand gebruikt wordt ───────────────────────────────────────── */

/** Een plek in de content waar een pad uit de media staat. */
export interface Plek {
  /** Wat het is, bv. Nieuwsbericht "Flex-E subsidie". */
  wat: string;
  /** Het formulier in de CRM, of null als die plek hier niet te bewerken is. */
  naar: string | null;
}

/** Een rij uit de content, met alle teksten erin waarin een pad uit de media staat. */
export interface Verwijzing extends Plek {
  tekst: string;
}

/* Een pad in de content kan gecodeerd zijn (%20) of niet: voor het vergelijken gaat de codering eraf. */
const ontcijfer = (tekst: string): string =>
  tekst.replace(/(?:%[0-9A-Fa-f]{2})+/g, (deel) => {
    try {
      return decodeURIComponent(deel);
    } catch {
      return deel;
    }
  });

/** Staat dit bestand in een afbeeldingsveld? Het pad in het veld mag gecodeerd zijn of niet. */
export const isGekozen = (bestand: Pick<MediaBestand, 'storage_path'>, waarde: string | null | undefined): boolean =>
  ontcijfer((waarde ?? '').trim()) === `${VOORVOEGSEL}${bestand.storage_path}`;

/** Alle teksten in een waarde waarin een pad uit de media staat: ook in een blok of in opgemaakte tekst. */
function teksten(waarde: unknown, uit: string[] = []): string[] {
  if (typeof waarde === 'string') {
    if (waarde.includes(VOORVOEGSEL)) uit.push(ontcijfer(waarde));
  } else if (Array.isArray(waarde)) waarde.forEach((deel) => teksten(deel, uit));
  else if (waarde && typeof waarde === 'object') Object.values(waarde).forEach((deel) => teksten(deel, uit));
  return uit;
}

const verwijzing = (rij: Rij, wat: string, naar: string | null): Verwijzing[] => {
  const gevonden = teksten(rij);
  return gevonden.length ? [{ wat, naar, tekst: gevonden.join('\n') }] : [];
};

/** Leest de content waarin een beeld kan staan: elke soort content, de pagina's en het logo uit de
 *  instellingen. Per rij blijft alleen over wat naar de media verwijst. Wat de code van de site zelf
 *  gebruikt, staat hier niet in: dat is in de database niet te zien. */
export async function haalVerwijzingen(client: WebsiteClient): Promise<Verwijzing[]> {
  const website = client.schema('website');
  const perSoort = await Promise.all(SOORTEN.map(async (soort) => {
    const rijen = ofFout<Rij[]>(await website.from(soort.tabel).select('*'), `${soort.meervoud} lezen`) ?? [];
    return rijen.flatMap((rij) => verwijzing(rij, `${soort.enkelvoud} "${rij[soort.titelKolom] ?? ''}"`, `/crm/website/site/${soort.sleutel}/${rij.id}`));
  }));
  // De vaste pagina's hebben een eigen formulier (./paginas).
  const paginas = ofFout<Rij[]>(await website.from('pages').select('*'), "Pagina's lezen") ?? [];
  // Alleen het logo: wat er verder in de instellingen staat, komt de browser niet in.
  const logo = ofFout<Rij[]>(await website.from('site_settings').select('key, value').eq('key', 'logo_url'), 'Instellingen lezen') ?? [];
  return [
    ...perSoort.flat(),
    ...paginas.flatMap((rij) => verwijzing(rij, `Pagina "${rij.title || rij.slug || ''}"`, `/crm/website/site/paginas/${rij.id}`)),
    ...logo.flatMap((rij) => verwijzing(rij, 'Instellingen: logo', '/crm/website/site/instellingen')),
  ];
}

/* Gaat de naam hierna nog door? Een punt telt alleen mee als er een letter of cijfer op volgt: zo is
 * foto.png niet foto.png.webp, en mag er na een pad in een zin gewoon een punt staan. */
const GAAT_DOOR = /^(?:[A-Za-z0-9_-]|\.[A-Za-z0-9])/;

/** De plekken in de content waar dit bestand staat. Leeg = niet gevonden in de content. */
export function gebruikVan(bestand: Pick<MediaBestand, 'storage_path'>, verwijzingen: Verwijzing[]): Plek[] {
  const pad = `${VOORVOEGSEL}${bestand.storage_path}`;
  const staatIn = (tekst: string): boolean => {
    for (let i = tekst.indexOf(pad); i !== -1; i = tekst.indexOf(pad, i + 1)) {
      if (!GAAT_DOOR.test(tekst.slice(i + pad.length, i + pad.length + 2))) return true;
    }
    return false;
  };
  return verwijzingen.filter((v) => staatIn(v.tekst)).map(({ wat, naar }) => ({ wat, naar }));
}
